










































































































































































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";
import PriceListsStore from "@/mixins/priceListsStore";

// Enums/Data
import { ProductTypes } from "@/data/enums/products";

// Types
import { IPromotionProduct } from "@/models/promotions";
import { IProduct } from "@/models/products";
import { DataModules } from "@/store/modules/data";

@Component({
  components: {
    "product-image": () =>
      import("@/components/app/global/products/productImage.vue")
  }
})
export default class SelectedPromotionProducts extends Mixins(
  RequestPromises,
  CurrenciesStore,
  BillingCyclesStore,
  PriceListsStore
) {
  // Props
  @Prop({ required: true })
  public selectedPromotionProducts!: IPromotionProduct[];

  @Prop({ default: false })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: false })
  public isMandatory!: boolean;

  // Data
  public isLoading = false;
  public isLoadingMore = false;
  public isReloading = false;
  public requestError = false;

  public limit = 5;
  public page = 1;

  public selectedProducts: IProduct[] = [];
  public preSelectedProducts: IProduct["id"][] = [];

  public action = null;
  public allSelectedProducts = false;

  // Lifecycle methods
  public async created() {
    if (!this.contentIsAvailable || !this.selectedPromotionProducts.length)
      return;

    this.isLoading = true;

    await Promise.all([
      this.getCurrencies(),
      this.getBillingCycles(),
      this.getPriceLists()
    ]);

    this.loadData();
  }

  //Getters

  public get hasNoProducts() {
    return !this.selectedProductIds.length;
  }

  public get tableProducts() {
    return this.$_.map(
      this.$_.slice(
        this.$_.drop(
          this.$_.cloneDeep(this.selectedPromotionProducts),
          this.offset
        ),
        0,
        this.limit
      ),
      selectedPromotionProduct =>
        this.$_.merge(selectedPromotionProduct, {
          product: this.$_.find(this.selectedProducts, [
            "id",
            selectedPromotionProduct.product_id
          ])
        })
    );
  }

  public get offset() {
    return (this.page - 1) * this.limit;
  }

  public get guardFunctionalities() {
    return ["list_products", "list_product_categories"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Methods
  public async loadData() {
    try {
      await this.loadMoreProducts(this.offset, this.limit);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public get selectedProductIds() {
    return this.$_.map(this.selectedPromotionProducts, "product_id");
  }

  public async loadMoreProducts(offset, limit) {
    try {
      this.isLoadingMore = true;

      const newProductsIds = this.$_.filter(
        this.$_.slice(this.$_.drop(this.selectedProductIds, offset), 0, limit),
        productId => !this.$_.some(this.selectedProducts, ["id", productId])
      ).join(",");

      if (!newProductsIds.length) {
        return Promise.resolve();
      }
      const newProducts = await this.getNewProducts(newProductsIds);
      this.selectedProducts = this.$_.concat(
        this.selectedProducts,
        newProducts
      );
      this.requestError = false;
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoadingMore = false;
    }
  }

  public getNewProducts(newProductsIds: string) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/list`, {
      params: {
        limit: 0,
        "filter[id|like]": newProductsIds,
        with: "image,provision_provider.logos"
      },
      storeData: false,
      returnData: true,
      vm: this
    });
  }

  public getProductCurrencies(product) {
    if (this.$_.isEmpty(product.currencies)) return this.$t("_.all");

    return this.$_.map(product.currencies, productCurrencyId => {
      return this.$_.get(
        this.$_.find(this.currencies, ["id", productCurrencyId]),
        "code",
        ""
      );
    }).join(", ");
  }

  public getPriceListsNames(product) {
    if (this.$_.isEmpty(product.pricelists)) return this.$t("_.all");

    return this.$_.map(product.pricelists, priceListId => {
      return this.$_.get(
        this.$_.find(this.priceLists, ["id", priceListId]),
        "name",
        ""
      );
    }).join(", ");
  }

  public getBillingTermsNames(product) {
    if (this.$_.isEmpty(product.billing_cycles)) return this.$t("_.all");

    return this.$_.map(product.billing_cycles, billingTerm => {
      return this.$_.get(
        this.$_.find(this.billingCycles, ["months", billingTerm]),
        "name",
        this.$t("_.every_months", { months: billingTerm })
      );
    }).join(", ");
  }

  public hasNoLimit(product) {
    return (
      this.$_.get(product, "min_quantity", null) === null &&
      this.$_.get(product, "max_quantity", null) === null
    );
  }

  public detach(product) {
    if (this.isDisabled || this.isProcessing) return;
    this.$emit("detachSelectedProducts", [product]);
  }

  public detachSelectedProducts() {
    this.$emit(
      "detachSelectedProducts",
      this.$_.filter(
        this.$_.cloneDeep(this.selectedPromotionProducts),
        selectedProduct =>
          this.preSelectedProducts.includes(
            selectedProduct.product_id as string
          )
      )
    );
  }

  public preSelect(productId) {
    if (this.preSelectedProducts.includes(productId)) {
      this.preSelectedProducts = this.$_.filter(
        this.preSelectedProducts,
        preSelectedProductId => preSelectedProductId !== productId
      );
    } else {
      this.preSelectedProducts.push(productId);
    }
  }

  public updateSelectedProducts() {
    this.$emit(
      "updateSelectedProducts",
      this.$_.map(this.preSelectedProducts, preSelectedProductId => {
        return {
          id: preSelectedProductId,
          product_id: preSelectedProductId
        };
      })
    );
  }

  public clearSelectedProducts() {
    this.$set(this, "preSelectedProducts", []);
  }

  public onPageChange(page: number) {
    this.page = page;
    this.loadMoreProducts(this.offset, this.limit);
  }

  public onLimitChange(limit: number) {
    this.limit = limit;
    this.page = 1;
    this.loadMoreProducts(this.offset, this.limit);
  }

  public isBundle(productType) {
    return productType === ProductTypes.PRODUCT_BUNDLE;
  }

  public selectAllProducts(value) {
    if (!value) {
      this.preSelectedProducts = [];
      return;
    }

    this.preSelectedProducts = this.$_.map(
      this.tableProducts,
      tableProduct => tableProduct.product_id
    ) as string[];
  }

  @Watch("preSelectedProducts")
  public onPreSelectedProductsChange() {
    this.$set(
      this,
      "allSelectedProducts",
      this.preSelectedProducts.length > 0 &&
        this.preSelectedProducts.length === this.tableProducts.length
    );
  }

  @Watch("action")
  public onActionChange() {
    if (!this.action) return;

    this.action === "update"
      ? this.updateSelectedProducts()
      : this.detachSelectedProducts();

    setTimeout(() => {
      this.$set(this, "action", null);
    }, 100);
  }

  @Watch("selectedProductIds")
  public onSelectedProductIdsChange() {
    if (this.tableProducts.length === 0) {
      this.page = this.page > 1 ? this.page - 1 : 0;
    }

    this.loadMoreProducts(this.offset, this.limit);
  }
}
